import './PulsanteVisualizzazione.css';

import TuneIcon from '@mui/icons-material/Tune';
import { Badge, Popover, Stack, ToggleButton, Typography } from '@mui/material';
import { memo, MouseEvent, useCallback, useId, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectPresenzeModo } from '../../hooks/usePresenzeModo';
import { selectSoloOrario } from '../../hooks/useSoloOrario';
import {
  CalendarioId,
  haPresenze,
} from '../../utility_objects/preferenzeCalendario';
import { PresenzeToggle } from '../Presenze/PresenzeToggle';
import { SoloOrarioToggle } from '../SoloOrario/SoloOrarioToggle';

interface PulsanteVisualizzazioneProps {
  /** Calendario della barra: le scelte del riquadro sono sue. */
  calendario: CalendarioId;
  /** Vista del calendario: Presenze è disabilitato nel Mese, Ore nel Mese e nell'Agenda. */
  view: string;
  /**
   * Riga Presenze: solo nei calendari con la fascia (agenda negozio,
   * giornaliero Studio), e solo con un punto vendita: senza, non c'è nessuno
   * da mostrare.
   */
  conPresenze: boolean;
  /**
   * Riga Ore. Nei calendari col filtro del punto vendita solo quando è scelto:
   * senza, non ci sono orari. Il Personale, che non ha il filtro del punto
   * vendita, la mostra sempre.
   */
  conOre: boolean;
  /**
   * Filtri del calendario non ancora caricati (punto vendita non ancora
   * scelto): il pulsante tiene il suo posto, disabilitato, così le viste non
   * scorrono a sinistra quando arriva il punto vendita.
   */
  disabilitato?: boolean;
}

// Pallino dentro l'angolo in alto a destra del pulsante: la barra sta a 2px
// dal bordo di BluContainerBlock, che ha overflow hidden, e un pallino a
// cavallo dell'angolo verrebbe tagliato
const SX_PALLINO = { '& .MuiBadge-badge': { top: 6, right: 6 } };

// Il pallino ripete quello che il nome del pulsante dice già
// («Visualizzazione, modificata»): nascosto agli screen reader. In una
// costante e non scritto nello slotProps: i tipi degli slot di MUI 5 non
// prevedono gli attributi data-*
const PROPS_PALLINO = {
  'data-testid': 'calendar-visualizzazione-pallino',
  'aria-hidden': true,
};

// Nome del pulsante: il testo a vista, e lo stato quando il calendario non è
// allo standard
const NOME = 'Visualizzazione';
const NOME_MODIFICATA = 'Visualizzazione, modificata';

// Riquadro largo 280px: dentro restano 248px, e il selettore Presenze a tre
// voci ne chiede circa 185
const SX_RIQUADRO = {
  mt: 0.5,
  width: 280,
  maxWidth: 'calc(100% - 32px)',
  p: 2,
};

/**
 * Pulsante e riquadro, montati solo quando c'è qualcosa da scegliere (o
 * mentre i filtri si caricano): se il pulsante sparisce (punto vendita
 * tolto), lo stato del riquadro sparisce con lui e non resta ancorato a un
 * elemento che non c'è più.
 */
const Pulsante = ({
  calendario,
  view,
  conPresenze,
  conOre,
  disabilitato = false,
}: PulsanteVisualizzazioneProps): JSX.Element => {
  const [ancora, setAncora] = useState<HTMLElement | null>(null);
  const idRiquadro = useId();
  const idTitolo = useId();
  const idDescrizione = useId();
  // Sola lettura, per il pallino: le scelte si cambiano nelle righe del
  // riquadro. Selettori del calendario: le scelte degli altri non contano
  const soloOrario = useSelector(selectSoloOrario(calendario));
  const modo = useSelector(selectPresenzeModo(calendario));

  const apri = useCallback(
    (evento: MouseEvent<HTMLElement>) => setAncora(evento.currentTarget),
    []
  );
  const chiudi = useCallback(() => setAncora(null), []);

  // Il Paper del riquadro è il dialog, nominato dal titolo e descritto dalla
  // riga sotto. In una variabile e non scritto nello slotProps, come per il
  // pallino
  const propsRiquadro = {
    id: idRiquadro,
    role: 'dialog',
    'aria-labelledby': idTitolo,
    'aria-describedby': idDescrizione,
    'data-testid': 'calendar-visualizzazione-riquadro',
    sx: SX_RIQUADRO,
  };

  const aperto = ancora !== null;
  // Diversa dallo standard (Presenze «Nessuno», Ore «Tutte») in una delle
  // righe che il calendario mostra; non dipende dalla vista: nel Mese, con
  // «Prenotabili», il pallino resta
  const modificata =
    (conPresenze && modo !== 'nessuna') || (conOre && soloOrario);

  return (
    <>
      <Badge
        variant="dot"
        color="primary"
        overlap="rectangular"
        invisible={!modificata}
        slotProps={{ badge: PROPS_PALLINO }}
        sx={SX_PALLINO}
      >
        {/* Premuto mentre il riquadro è aperto. aria-pressed={undefined}
            toglie quello che mette ToggleButton: è un pulsante che apre un
            riquadro, non un interruttore. Il nome è esplicito (aria-label) e
            comincia col testo a vista: da modificata è «Visualizzazione,
            modificata», come dice il pallino. Dal contenuto non verrebbe
            esatto: uno span nascosto con position absolute diventa un blocco,
            e Chromium mette uno spazio prima della virgola. Niente Tooltip:
            sotto il pulsante coprirebbe il riquadro */}
        <ToggleButton
          size="small"
          value="visualizzazione"
          selected={aperto}
          disabled={disabilitato}
          onClick={apri}
          aria-label={modificata ? NOME_MODIFICATA : NOME}
          aria-pressed={undefined}
          aria-haspopup="dialog"
          aria-expanded={aperto}
          aria-controls={aperto ? idRiquadro : undefined}
          data-testid="calendar-visualizzazione-pulsante"
          className="bb-visualizzazione"
        >
          <TuneIcon fontSize="small" className="bb-visualizzazione-icona" />
          <span className="bb-visualizzazione-testo">{NOME}</span>
        </ToggleButton>
      </Badge>
      {/* Esc e clic fuori chiudono; il focus torna al pulsante (restoreFocus
          di default). Le righe, coi loro salvataggi, si montano solo da
          aperto */}
      <Popover
        open={aperto}
        anchorEl={ancora}
        onClose={chiudi}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: propsRiquadro }}
      >
        <Stack spacing={1.5}>
          <Typography id={idTitolo} variant="subtitle1">
            Visualizzazione
          </Typography>
          {/* Nel colore del testo, non grigia: è l'istruzione che dice a
              cosa vale la scelta */}
          <Typography id={idDescrizione} variant="body2">
            Vale solo per questo calendario.
          </Typography>
          <Stack spacing={1}>
            {conPresenze && haPresenze(calendario) && (
              <PresenzeToggle calendario={calendario} mese={view === 'Month'} />
            )}
            {conOre && <SoloOrarioToggle calendario={calendario} view={view} />}
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};

/**
 * Pulsante «Visualizzazione» della barra delle viste, a destra delle viste
 * (nella console fra le viste e la stampa): icona Tune e testo, che nella
 * barra stretta si nasconde e lascia la sola icona (container query in
 * PulsanteVisualizzazione.css; il nome resta nell'aria-label). Il clic apre
 * il riquadro Visualizzazione, ancorato sotto il pulsante: titolo, la riga
 * «Vale solo per questo calendario.» e le righe Presenze (dove c'è la fascia,
 * con un punto vendita) e Ore. Le scelte sono del calendario (useSoloOrario,
 * usePresenzeModo, cookie in preferenzeCalendario): cambiarle in un calendario
 * non tocca gli altri. Il pallino dice che il calendario non è allo standard.
 * Senza niente da scegliere (agenda negozio o giornaliero senza punto
 * vendita, a filtri caricati) il pulsante non c'è; mentre i filtri si
 * caricano c'è, disabilitato.
 */
export const PulsanteVisualizzazione = memo(function PulsanteVisualizzazione(
  props: PulsanteVisualizzazioneProps
): JSX.Element | null {
  if (!props.disabilitato && !props.conPresenze && !props.conOre) return null;
  return <Pulsante {...props} />;
});
